import { Button, ButtonBase } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DASHBOARD_LABELS as LABELS } from '../../../utils/labels';
import type { OverdueReturnBooking } from '../../planning/types';

const MAX_VISIBLE = 4;
const MS_PER_HOUR = 1000 * 60 * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;

// overdueByMs can be well under an hour — isBookingOverdueReturn fires the instant the
// booking's end passes, with no minimum threshold — so both branches clamp to 1 rather than
// showing "0 uur" for a booking that just became overdue.
function formatOverdueBy(overdueByMs: number): string {
  if (overdueByMs >= MS_PER_DAY) {
    return LABELS.overdueReturnDays(Math.max(1, Math.floor(overdueByMs / MS_PER_DAY)));
  }
  return LABELS.overdueReturnHours(Math.max(1, Math.floor(overdueByMs / MS_PER_HOUR)));
}

interface OverdueReturnStatCardProps {
  overdueBookings: OverdueReturnBooking[];
  onNavigate: (machineIds: string[]) => void;
}

export default function OverdueReturnStatCard({ overdueBookings, onNavigate }: OverdueReturnStatCardProps) {
  const count = overdueBookings.length;

  if (count === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-8 text-emerald-700 dark:text-emerald-300">
        <CheckCircleOutlineIcon fontSize="large" />
        <span className="text-sm font-medium">{LABELS.overdueReturnAllOnTime}</span>
      </div>
    );
  }

  const allMachineIds = overdueBookings.map(({ machine }) => machine.id);
  const visible = overdueBookings.slice(0, MAX_VISIBLE);
  const hiddenCount = count - visible.length;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        {visible.map(({ machine, overdueByMs }) => (
          <ButtonBase
            key={machine.id}
            onClick={() => onNavigate([machine.id])}
            className="flex justify-start items-center gap-2 rounded-lg px-3 py-2 w-full text-left bg-red-50 dark:bg-red-950/40"
            data-testid={`overdue-return-machine-${machine.id}`}
          >
            <span className="text-sm font-medium truncate grow">{machine.name}</span>
            <span className="text-xs text-red-800 dark:text-red-300 shrink-0">
              {formatOverdueBy(overdueByMs)}
            </span>
            <ChevronRightIcon fontSize="small" className="text-text-secondary shrink-0" />
          </ButtonBase>
        ))}
        {hiddenCount > 0 && (
          <ButtonBase
            onClick={() => onNavigate(allMachineIds)}
            className="text-sm text-text-secondary text-left rounded-lg px-3 py-2 w-full"
            data-testid="overdue-return-more"
          >
            {LABELS.overdueReturnMore(hiddenCount)}
          </ButtonBase>
        )}
      </div>

      <Button
        variant="text"
        size="small"
        endIcon={<ArrowForwardIcon fontSize="small" />}
        onClick={() => onNavigate(allMachineIds)}
        className="self-start -ml-1 text-xs"
        data-testid="view-in-planning-button"
      >
        {LABELS.linkToPlanning}
      </Button>
    </div>
  );
}
